import { useState } from 'react';
import { useAppDispatch, useAppSelector } from '../../../Redux/hooks';
import { setSubscription } from '../../../Redux/sessionSlice';
import { useTranslation } from '../../../i18n/useTranslation';
import { ApiError } from '../../../Models/ApiModel';
import { SubscriptionService } from '../../../Services/SubscriptionService';
import { Spinner } from '../../SharedArea/Spinner/Spinner';
import './Premium.css';
import { PremiumSubnav } from './PremiumSubnav';
import { PaddleCheckout } from './PaddleCheckout';

/**
 * The subscription screen: what the tutor has, and how to change it.
 *
 * Cancelling and changing a card happen on Paddle's own portal rather than
 * here, because that is where the payment method actually lives.
 */
export function Premium() {
  const { tr, language } = useTranslation();
  const dispatch = useAppDispatch();
  const subscription = useAppSelector(state => state.session.subscription);
  const [error, setError] = useState<string | null>(null);

  /** Re-reads the entitlement from the server rather than assuming it. */
  async function refresh() {
    try {
      dispatch(setSubscription(await SubscriptionService.get()));
    } catch {
      // The stored state stays. A failed refresh is not a downgrade.
    }
  }

  /**
   * The portal URL only exists after a round trip, and window.open is honoured
   * only from a direct click, so the window is opened now and pointed at the
   * URL when it arrives.
   */
  function openPortal() {
    const portal = window.open('', '_blank');
    setError(null);
    SubscriptionService.portalSession()
      .then(session => {
        if (portal) portal.location.href = session.url;
        else window.location.href = session.url;
      })
      .catch(failure => {
        portal?.close();
        setError(failure instanceof ApiError ? failure.code : 'SERVER_ERROR');
      });
  }

  if (!subscription) return <Spinner />;

  const expiry = subscription.expiresAt ?? subscription.trialExpiresAt;

  return (
    <section className="premium">
      <PremiumSubnav />
      <h1 className="screen-title">{tr('premiumTitle')}</h1>

      <article className={subscription.active ? 'premium-state is-active' : 'premium-state'}>
        <p className="premium-state-label">
          {subscription.active ? tr('premiumActive') : tr('premiumLocked')}
        </p>
        {subscription.adminAccess && <p className="premium-state-note">{tr('premiumAdminAccess')}</p>}
        {expiry && (
          <p className="premium-state-note">
            {tr('premiumUntil')} <bdi>{new Date(expiry).toLocaleDateString(language === 'he' ? 'he-IL' : 'en-GB')}</bdi>
          </p>
        )}
      </article>

      <div className="premium-actions">
        {subscription.provider === 'paddle' && (
          <button type="button" className="ghost-button" onClick={openPortal}>
            {tr('premiumManageBilling')}
          </button>
        )}
      </div>

      {/* Only for someone who does not already have it. Selling a
          subscription to a subscriber is how a screen loses trust. */}
      {!subscription.active && <PaddleCheckout onPurchased={() => void refresh()} />}

      <section className="premium-features">
        <h2>{tr('premiumFeaturesTitle')}</h2>
        <ul>
          {subscription.features.map(feature => (
            <li key={feature.key} className={feature.accessible ? 'is-open' : 'is-locked'}>
              <span aria-hidden="true">{feature.accessible ? '✓' : '·'}</span>
              <span>{tr(featureLabel(feature.key))}</span>
            </li>
          ))}
        </ul>
      </section>

      {error && <p className="premium-error" role="alert">{tr('errorGeneric')}</p>}
    </section>
  );
}

function featureLabel(key: string) {
  switch (key) {
    case 'RECEIPTS': return 'navReceipts' as const;
    case 'ADVANCED_REPORTS': return 'navReports' as const;
    case 'EXPORTS': return 'featureExports' as const;
    case 'BACKUPS': return 'navBackup' as const;
    case 'BUSINESS_BRANDING': return 'featureBranding' as const;
    case 'PAYMENT_REMINDERS': return 'featureReminders' as const;
    case 'STUDENT_PROFILE': return 'featureStudentProfile' as const;
    default: return 'featureIntakeForms' as const;
  }
}
